/**
 * Page-section components for Hologram Club. Extracted from HoligramClub.jsx
 * as part of the file-split refactor.
 *
 * Exports:
 *   Nav, Hero, Stat, Ticker, NowPlaying, Meta, Schedule, Venue, Feature,
 *   Membership, Gallery, Footer
 */
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { mediaUrl } from "@/lib/http";
import { Trailer } from "@/components/club/Media";
import {
  Radio, MapPin, Users, Sparkles, ChevronRight, Zap, PlayCircle, Menu, X,
  Check, Instagram, Twitter, Youtube, LogIn, User, Shield,
} from "lucide-react";

const STATUS_COLORS = {
  live: "text-[#FF007F] border-[#FF007F]/60 bg-[#FF007F]/10",
  upcoming: "text-[#00E5FF] border-[#00E5FF]/50 bg-[#00E5FF]/5",
  soon: "text-white/70 border-white/20 bg-white/5",
};

/* ============================ NAV ============================ */
export function Nav({ onJoinClick, onReserveClick, venue, onVenueChange }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/hologram-club" data-testid="nav-logo" className="font-display text-xl md:text-2xl font-black tracking-tighter flex items-center gap-2">
          <span className="holofoil">HOLOGRAM</span>
          <span className="text-white">CLUB</span>
        </Link>

        <div className="hidden md:flex items-center gap-1 border border-white/15 p-1" data-testid="venue-switcher">
          {[{k:"vegas", l:"Vegas"}, {k:"la", l:"Los Angeles"}].map((v) => (
            <button
              key={v.k}
              onClick={() => onVenueChange(v.k)}
              data-testid={`venue-${v.k}`}
              className={`px-3 py-1.5 font-mono-ui text-[10px] tracking-[0.25em] uppercase transition-colors ${
                venue === v.k ? "bg-[#00E5FF] text-black" : "text-white/70 hover:text-white"
              }`}
            >
              {v.l}
            </button>
          ))}
        </div>

        <nav className="hidden lg:flex items-center gap-6 font-mono-ui text-xs uppercase tracking-[0.22em] text-white/70">
          <a href="#live" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-live">Live</a>
          <a href="#originals" className="hover:text-[#FFD166] transition-colors" data-testid="nav-originals">Originals</a>
          <a href="#schedule" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-schedule">Schedule</a>
          <a href="#venue" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-venue">Venue</a>
          <a href="#membership" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-membership">Club</a>
          <Link to="/hologram-club/demo" className="hover:text-[#FFD166] transition-colors text-[#FFD166]/90" data-testid="nav-investor-demo">Investor Demo</Link>
          <Link to="/hologram-club/license" className="hover:text-white transition-colors text-[#FF007F]" data-testid="nav-license">License</Link>
        </nav>

        <div className="flex items-center gap-2">
          {user === undefined ? null : user ? (
            <>
              {user.role === "admin" && (
                <>
                  <Link to="/hologram-club/admin?tab=music" className="text-[10px] py-2 px-3 hidden md:inline-flex items-center gap-2 border border-[#FFD166] bg-[#FFD166] text-black hover:bg-white hover:text-[#FFD166] transition-colors font-bold tracking-[0.22em] uppercase font-mono-ui" data-testid="nav-upload-music">
                    🎵 Upload MP3
                  </Link>
                  <Link to="/hologram-club/admin" className="text-[10px] py-2 px-3 inline-flex items-center gap-2 border border-[#FF007F] bg-[#FF007F] text-black hover:bg-white hover:text-[#FF007F] transition-colors font-bold tracking-[0.22em] uppercase font-mono-ui" data-testid="nav-admin">
                    <Shield size={12} /> Admin
                  </Link>
                </>
              )}
              <Link to="/hologram-club/my-reservations" className="btn-ghost text-[10px] py-2 px-3 inline-flex items-center gap-2" data-testid="nav-account">
                <User size={12} /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button onClick={logout} className="hidden sm:inline font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50 hover:text-[#FF007F] px-2" data-testid="nav-logout">
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link to="/hologram-club/login" className="hidden sm:inline-flex btn-ghost text-[10px] py-2 px-3 items-center gap-2" data-testid="nav-login">
                <LogIn size={12} /> Log In
              </Link>
              <button onClick={onJoinClick} data-testid="nav-join-btn" className="btn-neon text-[11px] py-2.5 px-4">
                Join Club
              </button>
            </>
          )}

          <button onClick={onReserveClick} data-testid="nav-reserve-btn" className="hidden md:inline-flex btn-ghost text-[10px] py-2 px-3">
            Reserve
          </button>

          <button className="lg:hidden text-white p-2" data-testid="nav-menu-btn" onClick={() => setOpen((v) => !v)}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
          <div className="flex flex-col py-4 px-6 gap-4 font-mono-ui text-xs uppercase tracking-[0.22em] text-white/80">
            <div className="flex gap-2" data-testid="venue-switcher-mobile">
              {[{k:"vegas", l:"Vegas"}, {k:"la", l:"LA"}].map((v) => (
                <button key={v.k} onClick={() => { onVenueChange(v.k); setOpen(false); }} className={`px-3 py-1.5 border ${venue === v.k ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "border-white/15"}`}>{v.l}</button>
              ))}
            </div>
            <a href="#live" onClick={() => setOpen(false)}>Live</a>
            <a href="#originals" onClick={() => setOpen(false)} className="text-[#FFD166]">Originals</a>
            <a href="#schedule" onClick={() => setOpen(false)}>Schedule</a>
            <a href="#venue" onClick={() => setOpen(false)}>Venue</a>
            <a href="#membership" onClick={() => setOpen(false)}>Club</a>
            <Link to="/hologram-club/demo" onClick={() => setOpen(false)} className="text-[#FFD166] border border-[#FFD166]/40 px-3 py-2 text-center" data-testid="nav-investor-demo-mobile">▶ Investor Demo</Link>
            <Link to="/hologram-club/license" onClick={() => setOpen(false)} className="text-[#FF007F] border border-[#FF007F]/40 px-3 py-2 text-center" data-testid="nav-license-mobile">License & Invest</Link>
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/hologram-club/admin" onClick={() => setOpen(false)} className="bg-[#FF007F] text-black text-center py-2 font-bold" data-testid="nav-admin-mobile">
                    🎛 Admin · Upload MP3
                  </Link>
                )}
                <Link to="/hologram-club/my-reservations" onClick={() => setOpen(false)}>My Account</Link>
              </>
            ) : (
              <Link to="/hologram-club/login" onClick={() => setOpen(false)}>Log In</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

/* ============================ HERO ============================ */
export function Hero({ onJoinClick, onReserveClick, stats, onInvestorReelClick }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden isolate">
      <motion.div style={{ y, opacity }} className="absolute inset-0 -z-10">
        <img src="https://images.unsplash.com/photo-1765279113604-efe3b0301134" alt="laser venue" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#050505]" />
        <div className="absolute inset-0 scanlines opacity-40" />
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-28 md:pt-40 pb-16 md:pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-flex items-center gap-3 border border-[#FF007F]/50 bg-[#FF007F]/10 px-3 py-1.5 md:px-4 md:py-2 mb-6 md:mb-8" data-testid="hero-live-badge">
          <span className="ring-dot" />
          <span className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-[#FF007F]">Live Now — 24/7 — Las Vegas</span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="font-display font-black tracking-tighter leading-[0.88] text-[44px] xs:text-[52px] sm:text-[88px] md:text-[128px] lg:text-[160px]">
          <span className="block text-white">HOLOGRAM</span>
          <span className="block holofoil flicker">CLUB.</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="mt-5 md:mt-6 max-w-2xl text-[15px] leading-relaxed md:text-xl text-white/75 font-body" data-testid="hero-tagline">
          A 24/7 volumetric concert venue on the Las Vegas
          Strip. Elvis at midnight. Whitney at 2 a.m. 2Pac, Amy, Freddie,
          Prince — reconstructed in photonic light, performing to a full house,
          every hour of every night.
        </motion.p>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }} className="mt-8 md:mt-10 grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-3 md:gap-4">
          <button onClick={onReserveClick} data-testid="hero-reserve-btn" className="btn-neon w-full sm:w-auto min-h-[52px]">Reserve Tonight</button>
          <button onClick={onJoinClick} data-testid="hero-join-btn" className="btn-ghost w-full sm:w-auto min-h-[52px]">Join The Club</button>
          {onInvestorReelClick && (
            <button
              onClick={onInvestorReelClick}
              data-testid="hero-investor-reel-btn"
              className="group relative font-mono-ui text-[11px] tracking-[0.28em] md:tracking-[0.32em] uppercase font-bold py-3.5 px-5 min-h-[52px] border border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166] hover:bg-[#FFD166] hover:text-black transition-colors inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              title="Auto-cycle through all 20 holograms (~2:30)"
            >
              <span className="ring-dot bg-[#FFD166]" /> Investor Reel
            </button>
          )}
          <a href="#live" className="hidden sm:inline-flex font-mono-ui text-[11px] tracking-[0.25em] uppercase text-white/60 hover:text-[#00E5FF] transition-colors items-center gap-2" data-testid="hero-watch-live">
            <PlayCircle size={16} /> Watch the floor
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }} className="mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="hero-stats">
          <Stat label="Watching Now" value={stats?.viewers ?? "—"} accent />
          <Stat label="Countries" value={stats?.countries ?? "—"} />
          <Stat label="Photon Emitters" value={stats?.photon_emitters_online ?? "—"} />
          <Stat label="Floor Temp" value={stats?.floor_temperature_c ? `${stats.floor_temperature_c}°C` : "—"} />
        </motion.div>
      </div>
    </section>
  );
}

export function Stat({ label, value, accent = false }) {
  return (
    <div className="bg-[#0B0C10] p-5 md:p-7">
      <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">{label}</div>
      <div className={`mt-2 font-display text-2xl md:text-4xl font-bold ${accent ? "text-cyan-glow" : "text-white"}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

/* ============================ TICKER ============================ */
export function Ticker() {
  const line = "HOLOGRAM CLUB  •  24/7 HOLOGRAM PERFORMANCES  •  LAS VEGAS STRIP  •  NEVER DARK  •  NEVER CLOSED  •  NEVER OVER  •  ";
  return (
    <div data-testid="marquee-ticker" className="border-y border-white/10 bg-[#0B0C10] py-4">
      <Marquee speed={60} gradient={false} className="font-display font-black tracking-tight text-2xl md:text-3xl">
        <span className="text-white/90 mx-6">{line}</span>
        <span className="text-[#00E5FF] mx-6">{line}</span>
        <span className="text-white/90 mx-6">{line}</span>
        <span className="text-[#FF007F] mx-6">{line}</span>
      </Marquee>
    </div>
  );
}

/* ============================ NOW PLAYING ============================ */
export function NowPlaying({ show, stats }) {
  if (!show) return null;
  return (
    <section id="live" data-testid="now-playing" className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF] flex items-center gap-3">
            <span className="ring-dot" />
            <span>Transmission 01 — On Air</span>
          </div>
          <h2 className="mt-5 font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">
            Now <br /><span className="holofoil">Playing.</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-md">
            Our holo-floor is live 24/7. Right now, in The Hologramme, {show.artist} is performing
            a full set to a sold-out room. Tune the photons in from anywhere — or walk in.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-3 font-mono-ui text-[11px] tracking-[0.22em] uppercase">
            <Meta label="Artist" value={show.artist} />
            <Meta label="Era" value={show.era} />
            <Meta label="Slot" value={show.slot} />
            <Meta label="Viewers" value={stats?.viewers?.toLocaleString() ?? "—"} />
          </div>
          <a href="#schedule" className="mt-10 inline-flex items-center gap-2 font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[#00E5FF] hover:text-white transition-colors" data-testid="nowplaying-full-schedule">
            Full Schedule <ChevronRight size={14} />
          </a>
        </div>

        <div className="lg:col-span-7 relative">
          <div className="relative aspect-[4/3] overflow-hidden card-surface gradient-border">
            <Trailer src={show.trailer_url} poster={show.image} className="w-full h-full" testId="nowplaying-trailer" eager allowAudio />
            <div className="absolute inset-0 bg-gradient-to-tr from-black via-transparent to-transparent pointer-events-none" />
            <div className="absolute inset-0 scanlines opacity-30 pointer-events-none" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 border border-[#FF007F]/60 bg-black/60 backdrop-blur px-3 py-1.5">
              <span className="ring-dot" />
              <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF007F]">Live</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 pointer-events-none">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">{show.era} • {show.slot}</div>
              <div className="mt-2 font-display font-black tracking-tighter text-3xl md:text-5xl">{show.artist}</div>
              <div className="mt-2 text-white/70 text-sm md:text-base max-w-xl">{show.tagline}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Meta({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#0B0C10] p-3">
      <div className="text-white/40 text-[9px] tracking-[0.28em]">{label}</div>
      <div className="mt-1 text-white text-sm font-mono-ui">{value}</div>
    </div>
  );
}

/* ============================ SCHEDULE ============================ */
export function Schedule({ shows, onReserve, onWatch }) {
  return (
    <section id="schedule" data-testid="schedule-section" className="relative py-20 md:py-28 border-y border-white/5 bg-[#07080C]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Real-Time Trailers · 24/7</div>
            <h2 className="mt-4 font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">
              Tonight's <br /> <span className="holofoil">Lineup.</span>
            </h2>
          </div>
          <div className="text-white/60 max-w-md font-body">
            Eight sets, both venues, looping live. Each trailer plays in real
            time — Resident & VIP stream every show free; Spectators can pay-per-view.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {shows.map((s, i) => {
            const ppv = Number(s.ppv_price_usd || 0);
            const cardImage = s.character_image_url ? mediaUrl(s.character_image_url) : s.image;
            return (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`show-card-${s.id}`}
                className="relative card-surface shine overflow-hidden group flex flex-col"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={cardImage}
                    alt={s.artist}
                    className="absolute inset-0 w-full h-full object-cover ken-burns hologram-isolated hologram-projection opacity-95 group-hover:opacity-100 transition-opacity duration-700"
                    data-testid={`card-img-${s.id}`}
                  />
                  <Trailer
                    src={s.trailer_url}
                    poster={cardImage}
                    className="absolute inset-0 w-full h-full object-cover mix-blend-screen opacity-25 group-hover:opacity-40 transition-opacity duration-700"
                    testId={`trailer-${s.id}`}
                  />
                  <div className="absolute inset-0 scanlines opacity-30 pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent pointer-events-none" />
                  <div className="absolute top-3 left-3 flex flex-col gap-2">
                    <span className={`inline-flex items-center gap-1.5 border px-2 py-1 font-mono-ui text-[9px] tracking-[0.28em] uppercase ${STATUS_COLORS[s.status]}`}>
                      {s.status === "live" && <span className="ring-dot" style={{ width: 6, height: 6 }} />}
                      {s.status}
                    </span>
                    {s.is_original && (
                      <span className="inline-flex items-center gap-1.5 border border-[#FFD166]/60 bg-[#FFD166]/10 text-[#FFD166] px-2 py-1 font-mono-ui text-[9px] tracking-[0.28em] uppercase" data-testid={`original-badge-${s.id}`}>
                        Original
                      </span>
                    )}
                  </div>
                  {ppv > 0 && (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 border border-[#00E5FF]/60 bg-black/70 backdrop-blur px-2 py-1 font-mono-ui text-[10px] tracking-[0.18em] text-[#00E5FF]">
                      PPV ${ppv.toFixed(2)}
                    </div>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">{s.slot}</div>
                  <div className="mt-2 font-display font-black tracking-tighter text-2xl">{s.artist}</div>
                  <div className="mt-1 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-white/50">{s.era}</div>
                  <p className="mt-3 text-sm text-white/60 line-clamp-2 flex-1">{s.tagline}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button onClick={() => onReserve(s)} data-testid={`reserve-${s.id}`} className="inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/80 hover:text-[#00E5FF] transition-colors">
                      Reserve Table <ChevronRight size={13} />
                    </button>
                    {(s.audio_url || ppv > 0) && (
                      <button onClick={() => onWatch(s)} data-testid={`watch-${s.id}`} className="inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[#FF007F] hover:text-white transition-colors">
                        <PlayCircle size={13} /> {ppv > 0 ? `Watch · $${ppv.toFixed(2)}` : "Listen"}
                      </button>
                    )}
                    {s.youtube_url && (
                      <button type="button" onClick={() => onWatch(s)} data-testid={`yt-${s.id}`} className="inline-flex items-center gap-1.5 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-[#FFD166] transition-colors" title={`Watch ${s.artist} inside the app`}>
                        <Youtube size={13} /> Watch
                      </button>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================ VENUE ============================ */
export function Venue({ stats, venue }) {
  const isLA = venue?.id === "la";
  return (
    <section id="venue" data-testid="venue-section" className="relative py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6 relative">
          <div className="relative aspect-[5/6] overflow-hidden card-surface">
            <img src={venue?.hero || "https://images.unsplash.com/photo-1671573576055-b6ac57f8ec51"} alt={venue?.name || "Venue"} className="w-full h-full object-cover" key={venue?.id} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
            <div className="absolute inset-0 scanlines opacity-25" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
              <div>
                <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">Coordinates</div>
                <div className="mt-1 font-display font-bold text-2xl" data-testid="venue-coords">{venue?.coords || "—"}</div>
              </div>
              <MapPin className="text-[#00E5FF]" />
            </div>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]" data-testid="venue-name">
            {venue?.name || "The Venue"}
          </div>
          <h2 className="mt-4 font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">
            {isLA ? <>Sunset Boulevard's <br /> <span className="holofoil">photon palace.</span></> : <>Built inside <br /> a <span className="holofoil">photon field.</span></>}
          </h2>
          <p className="mt-6 text-white/70 max-w-lg">
            {isLA
              ? "The LA outpost lives mid–West Hollywood, in a former art-deco theatre rebuilt as a 1,440-seat volumetric stage. Same 24/7 lineup logic as Vegas — different time zone, different crowd, same legends."
              : "The Hologramme sits mid-Strip, eight stories tall, wrapped in a volumetric display skin. Inside: a 71,982-emitter floor, a 360° surround pit, and twelve resident artists whose light-based archives rotate every two hours."}
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <Feature icon={<Zap size={16} />} title="71,982 emitters" subtitle="9.8 trillion photons / sec" />
            <Feature icon={<Users size={16} />} title={`${(venue?.capacity || 1920).toLocaleString()} seats`} subtitle="360° surround pit" />
            <Feature icon={<Radio size={16} />} title="24 / 7 on-air" subtitle="Never goes dark" />
            <Feature icon={<MapPin size={16} />} title={venue?.city || "Las Vegas, NV"} subtitle={venue?.address || "3720 Las Vegas Blvd"} />
          </div>

          <div className="mt-10 p-5 border border-[#00E5FF]/25 bg-[#00E5FF]/5 font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[#00E5FF]">
            Server time (UTC):&nbsp;
            <span className="text-white">
              {stats?.server_time_utc ? new Date(stats.server_time_utc).toISOString().slice(11, 19) : "—"}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Feature({ icon, title, subtitle }) {
  return (
    <div className="border border-white/10 bg-[#0B0C10] p-5 hover:border-[#00E5FF]/40 transition-colors">
      <div className="text-[#00E5FF]">{icon}</div>
      <div className="mt-3 font-display font-bold text-lg">{title}</div>
      <div className="mt-1 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/50">{subtitle}</div>
    </div>
  );
}

/* ============================ MEMBERSHIP ============================ */
export function Membership({ tiers, onJoinClick, checkingOut }) {
  return (
    <section id="membership" data-testid="membership-section" className="relative py-16 md:py-28 border-t border-white/5 bg-[#07080C]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="mb-10 md:mb-14">
          <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">The Club</div>
          <h2 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-7xl leading-[0.9]">
            Pick your <br /> <span className="holofoil">ticket to forever.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4 pt-3">
          {tiers.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              data-testid={`tier-${t.id}`}
              className={`relative card-surface p-6 md:p-10 flex flex-col ${t.id === "vip_immortal" ? "border-[#FF007F]/40" : ""}`}
            >
              {t.id === "vip_immortal" && (
                <div className="absolute -top-3 left-6 md:left-8 inline-flex items-center gap-2 bg-[#FF007F] text-black font-mono-ui text-[10px] tracking-[0.22em] md:tracking-[0.25em] uppercase px-2.5 py-1 md:px-3">
                  <Sparkles size={12} /> Recommended
                </div>
              )}
              <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-white/50">Tier / {String(i + 1).padStart(2, "0")}</div>
              <div className="mt-3 md:mt-4 font-display font-black tracking-tighter text-2xl md:text-4xl">{t.name}</div>
              <div className="mt-3 md:mt-4 flex items-baseline gap-2">
                <span className="font-display font-black text-4xl md:text-6xl text-cyan-glow">${t.price_usd}</span>
                <span className="font-mono-ui text-[11px] uppercase tracking-[0.2em] text-white/50">{t.cadence}</span>
              </div>
              <ul className="mt-6 md:mt-8 space-y-3 flex-1">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[15px] md:text-sm text-white/80 md:text-white/75 leading-snug">
                    <Check size={16} className="text-[#00E5FF] mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onJoinClick(t.id)}
                data-testid={`join-${t.id}`}
                disabled={checkingOut === t.id}
                className={`mt-8 md:mt-10 min-h-[52px] disabled:opacity-60 ${t.id === "vip_immortal" ? "btn-neon" : "btn-ghost"}`}
              >
                {checkingOut === t.id
                  ? "Redirecting to Stripe..."
                  : t.price_usd === 0
                  ? "Join Free"
                  : "Subscribe " + t.name.split(" ")[0]}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ GALLERY ============================ */
const GALLERY = [
  { src: "https://images.unsplash.com/photo-1760965824676-085031285221", caption: "Opening Night — 24 Dec '25" },
  { src: "https://images.unsplash.com/photo-1729554608003-5ec8be42da1d", caption: "Photon calibration — Floor B" },
  { src: "https://images.unsplash.com/photo-1704253797699-f983e3cd70ff", caption: "Elvis, Tuesday 02:14" },
  { src: "https://images.unsplash.com/photo-1765279113604-efe3b0301134", caption: "Freddie — Live Aid resurrected" },
  { src: "https://images.unsplash.com/photo-1671573576055-b6ac57f8ec51", caption: "The Strip — 3 a.m." },
];

export function Gallery() {
  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Archive</div>
            <h2 className="mt-4 font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">
              Nights, <br /><span className="holofoil">caught in light.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 grid-rows-2 gap-3 md:gap-4 h-[520px] md:h-[640px]">
          {GALLERY.map((g, i) => {
            const cls = [
              "col-span-7 row-span-2",
              "col-span-5 row-span-1",
              "col-span-3 row-span-1",
              "col-span-2 row-span-1",
              "col-span-12 row-span-1 md:col-span-5 md:row-span-1 hidden md:block",
            ][i] || "col-span-6 row-span-1";
            return (
              <figure key={i} data-testid={`gallery-item-${i}`} className={`${cls} relative overflow-hidden card-surface shine group`}>
                <img src={g.src} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-85" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <figcaption className="absolute bottom-3 left-3 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/80">
                  {g.caption}
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================ FOOTER ============================ */
export function Footer({ memberCount }) {
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-display text-3xl font-black tracking-tighter">
            <span className="holofoil">HOLOGRAM</span> CLUB.
          </div>
          <p className="mt-4 text-white/60 max-w-md">
            A 24/7 hologram music venue on the Las Vegas Strip. Never dark.
            Never closed. {memberCount ? memberCount.toLocaleString() : "—"} members on the guest list and growing.
          </p>
          <div className="mt-6 flex gap-4">
            <a data-testid="social-instagram" href="#" className="w-10 h-10 border border-white/15 hover:border-[#00E5FF] hover:text-[#00E5FF] flex items-center justify-center transition-colors"><Instagram size={16} /></a>
            <a data-testid="social-twitter"   href="#" className="w-10 h-10 border border-white/15 hover:border-[#00E5FF] hover:text-[#00E5FF] flex items-center justify-center transition-colors"><Twitter size={16} /></a>
            <a data-testid="social-youtube"   href="#" className="w-10 h-10 border border-white/15 hover:border-[#00E5FF] hover:text-[#00E5FF] flex items-center justify-center transition-colors"><Youtube size={16} /></a>
          </div>
        </div>
        <div className="md:col-span-3">
          <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">Venue</div>
          <div className="mt-3 space-y-2 text-white/75 text-sm">
            <div>The Hologramme</div>
            <div>3720 Las Vegas Blvd</div>
            <div>Las Vegas, NV 89109</div>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">Navigate</div>
          <div className="mt-3 space-y-2 text-white/75 text-sm">
            <div><a href="#live" className="hover:text-[#00E5FF]">Live</a></div>
            <div><a href="#schedule" className="hover:text-[#00E5FF]">Schedule</a></div>
            <div><a href="#membership" className="hover:text-[#00E5FF]">Membership</a></div>
            <div><a href="#gallery" className="hover:text-[#00E5FF]">Gallery</a></div>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">Status</div>
          <div className="mt-3 space-y-2 text-white/75 text-sm font-mono-ui">
            <div className="flex items-center gap-2"><span className="ring-dot" style={{ width: 8, height: 8 }} /> On Air</div>
            <div>24 / 7 / 365</div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">
        © {new Date().getFullYear()} Hologram Club — All photons reserved.
      </div>
    </footer>
  );
}
